import {
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { ConfirmationDialogContext } from 'contexts/confirmationDialog';
import compact from 'lodash/compact';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import { parseErrorMessage } from 'utils/helper';
import {
  InsuranceDTO,
  InsuranceSlot,
  InsuranceSlotOptionDTO,
} from 'artifacts/dtos';
import { useGetInsuranceSettings as useGetInsuranceSettingsQuery } from 'hooks/useApi';

export function useConfirmDialog() {
  const { openDialog, closeDialog } = useContext(ConfirmationDialogContext);
  return {
    confirm: openDialog,
    closeConfirm: closeDialog,
  };
}

export function useGetInsuranceSettings() {
  const {
    data,
    error,
    isLoading,
    isFetching,
  } = useGetInsuranceSettingsQuery(true);

  const errorMessage = useMemo(
    () => (error ? parseErrorMessage(error) : ''),
    [error],
  );

  const { mspTypes = [], relationshipTypes, insuranceSlotOptions = [] } = data ?? {};

  const relationshipTypesOptions = useMemo(
    () => compact(map(relationshipTypes, 'name')),
    [relationshipTypes],
  ) ?? [];

  const relationshipTypesByName = useMemo(
    () => keyBy(relationshipTypes ?? [], 'name'),
    [relationshipTypes],
  );

  const insuranceSlotValues = useMemo(
    () => compact(map(insuranceSlotOptions, 'slot')) as InsuranceSlot[],
    [insuranceSlotOptions],
  );

  const insuranceSlotOptionsBySlot = useMemo(
    () => keyBy(insuranceSlotOptions ?? [], 'slot') as Partial<Record<InsuranceSlot, InsuranceSlotOptionDTO>>,
    [insuranceSlotOptions],
  );

  const getInsuranceSlotLabel = useCallback(
    (slot: InsuranceSlot) => insuranceSlotOptionsBySlot[slot]?.displayName ?? slot,
    [insuranceSlotOptionsBySlot],
  );

  return {
    isLoading: isLoading || isFetching,
    errorMessage,
    relationshipTypes: relationshipTypesOptions,
    relationshipTypesByName,
    mspTypes,
    insuranceSlotOptions: insuranceSlotOptions ?? [],
    insuranceSlotValues,
    getInsuranceSlotLabel,
  };
}

type RestrictedInsuranceSlotConflictParams = {
  currentInsuranceId?: number;
  selectedInsuranceSlot?: InsuranceSlot | null;
  isActiveInsurance: boolean;
};

export function useRestrictedInsuranceSlotConflict(
  insurances: InsuranceDTO[] = [],
  restrictedSlotSet: Set<InsuranceSlot | undefined> = new Set(),
) {
  return useCallback(({
    currentInsuranceId,
    selectedInsuranceSlot,
    isActiveInsurance,
  }: RestrictedInsuranceSlotConflictParams) => Boolean(
    isActiveInsurance
    && !isNil(selectedInsuranceSlot)
    && restrictedSlotSet.has(selectedInsuranceSlot as InsuranceSlot)
    && insurances.some((insuranceItem) => (
      (insuranceItem?.insuranceId ?? 0) !== (currentInsuranceId ?? 0)
      && !insuranceItem?.inactive
      && insuranceItem?.insuranceSlot === selectedInsuranceSlot
    )),
  ), [insurances, restrictedSlotSet]);
}
